import React, { useState, useRef } from 'react'
import { useTextField } from '@react-aria/textfield'
import { useCreateBrandKitSync } from 'hooks/queries/useCreateBrandKitSync'
import { useCreateSource } from 'hooks/queries/workspaces/useCreateSource'
import { useIsUrlCrawlable } from 'hooks/useIsUrlCrawlable'
import { reportError } from 'utils/error'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { BrandKit, ExampleBrand } from 'types'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import useURLParams from 'hooks/useURLParams'
import { Processing } from './Processing'

import { BrandKitContext } from './BrandKitContext'
import { BrandKitForm } from './BrandKitForm'
import { IntegrationFailedBrandKitForm } from './IntegrationFailedBrandKitForm'
import { WebsiteFailedBrandKitForm } from './WebsiteFailedBrandKitForm'
import { integrationToName } from 'components/IntegrationRequiredModal/IntegrationRequiredModal'
import { BrandKitMultiplePagesForm } from './BrandKitMultiplePagesForm'
import { ImagesProvider } from 'components/Wizards/SelectImages/ImagesProvider'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { normalizeUrl } from './utils'
import { useCreateWebsiteCrawl } from 'hooks/queries/workspaces/useCreateWebsiteCrawl'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

interface BrandKitParentProps {
  onBrandKitGenerated: (brandKit: BrandKit) => void
  onNext: () => void
  onBack?: () => void
  onSkip?: () => void
  onWebsiteAnalysisFailed?: () => void
  onIntegrationFailed?: (integration: { id: number; name: string; provider: ExternalIntegrationEnum }) => void
  showTitles?: boolean
  withinOnboarding?: boolean
  useIntegrations?: boolean
  onSubmit?: (value: boolean) => void
}

/**
 * Central "parent" that:
 *  - Holds all the brand kit creation logic, queries, states
 *  - Puts them into a BrandKitContext so children only handle UI
 *  - Decides whether to show the normal BrandKitForm or the fallback IntegrationFailedBrandKitForm
 *  - Displays the "Processing" screen if brand kit creation is in progress
 */
export const BrandKitParent: React.FC<BrandKitParentProps> = ({
  onBrandKitGenerated,
  onNext,
  onBack,
  onSkip,
  onIntegrationFailed,
  onWebsiteAnalysisFailed,
  onSubmit,
  showTitles = true,
  withinOnboarding = false,
  useIntegrations = true,
}) => {
  const [url, setUrl] = useState('')
  const [isDirty, setIsDirty] = useState(false)
  const [searchParams] = useURLParams()
  const { isMobile } = useResponsiveDesign()
  const [failedIntegration, setFailedIntegration] = useState<{
    id: number
    name: string
    provider: ExternalIntegrationEnum
  } | null>(null)
  const [websiteAnalysisFailed, setWebsiteAnalysisFailed] = useState(false)
  const [isGeneratingFromExampleBrand, setIsGeneratingFromExampleBrand] = useState<boolean>(false)
  const [needsMoreData, setNeedsMoreData] = useState(false)
  const [brandKit, setBrandKit] = useState<BrandKit | null>(null)
  const useWebsiteCrawler = useFlipperFlag('website_crawler')

  // Add a state to track if processing is active
  const [isProcessing, setIsProcessing] = useState(false)
  const platform = searchParams.get('contentCreationSubcategory') as ExternalIntegrationEnum | null | string

  // Validate the user's URL
  const { isValid, errorMessage } = useIsUrlCrawlable(url.trim())
  const isBlank = url.trim() === ''

  // For canceling brand kit creation if the user closes or navigates away
  const abortController = useRef(new AbortController())

  // 1. Brand kit creation hook (polls for progress)
  const createBrandKit = useCreateBrandKitSync({ mutationKey: 'createNewBrandKit' })

  // 2. "Create source" hook for website usage
  const createSource = useCreateSource()
  const createWebsiteCrawl = useCreateWebsiteCrawl()
  const mutation = useWebsiteCrawler ? createWebsiteCrawl : createSource

  // 3. React Aria textfield setup
  const inputRef = useRef<HTMLInputElement>(null)
  const { inputProps: rawInputProps } = useTextField(
    {
      label: 'Website URL',
      description: 'We will learn your brand voice from your website content.',
      isInvalid: isDirty && !isValid,
      onChange: setUrl,
    },
    inputRef,
  )
  const inputProps: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'name'> & { name: string } = {
    ...rawInputProps,
    name: rawInputProps.name || 'website',
  }

  // ------------------
  // Callback Handlers
  // ------------------

  /** Generate from user's website URL */
  const onGenerateFromWebsite = async () => {
    setIsGeneratingFromExampleBrand(false)
    setIsDirty(true)
    if (!isValid || isBlank) return

    try {
      setIsProcessing(true)
      const { source } = await mutation.mutateAsync({ type: 'Website', url: normalizeUrl(url) })
      await handleGenerateBrandKit({
        name: url,
        sources: [{ type: 'Website', id: source.id }],
      })
    } catch (err) {
      setUrl('')
      // Only set websiteAnalysisFailed if this wasn't a manual cancellation
      if (!(err instanceof DOMException && err.name === 'AbortError')) {
        onWebsiteAnalysisFailed?.()
        setWebsiteAnalysisFailed(true)
      }
      setIsProcessing(false)
      handleError(err)
    }
  }

  /** Generate from a sample brand */
  const onGenerateFromExampleBrand = async (brand: ExampleBrand) => {
    setIsGeneratingFromExampleBrand(true)
    try {
      setIsProcessing(true)
      await handleGenerateBrandKit({
        name: brand.name,
        sources: [{ type: 'ExampleBrand', id: brand.id }],
      })
    } catch (err) {
      setIsProcessing(false)
      handleError(err)
    }
  }

  /** Generate from a newly connected integration */
  const onGenerateFromIntegration = async (integration: {
    id: number
    name: string
    provider: ExternalIntegrationEnum
  }) => {
    try {
      setIsProcessing(true)
      await handleGenerateBrandKit({
        name: integration.name,
        sources: [{ type: 'ExternalIntegration', id: integration.id }],
      })
    } catch (err) {
      // Only set failedIntegration if this wasn't a manual cancellation
      if (!(err instanceof DOMException && err.name === 'AbortError')) {
        onIntegrationFailed?.(integration)
        setFailedIntegration(integration)
      }
      setIsProcessing(false)
      handleError(err)
    }
  }

  /**
   * Actually calls createBrandKit with the chosen source(s).
   * If successful, calls onNext to move on.
   */
  const handleGenerateBrandKit = async (variables: { name: string; sources: { id: number; type: string }[] }) => {
    const brandKit = await createBrandKit.mutateAsync({
      signal: abortController.current.signal,
      ...variables,
      sources: variables.sources.map((source) => ({
        ...source,
        type: source.type as 'Website' | 'ExampleBrand' | 'ExternalIntegration',
      })),
    })
    setIsProcessing(false)
    setBrandKit(brandKit)
    onBrandKitGenerated(brandKit)

    // If the brand kit has less than 4 media files, we need to show the "Needs More Data" UI
    if (brandKit.media_files.length < 4 && !isMobile && useWebsiteCrawler) {
      setNeedsMoreData(true)
    } else {
      onNext()
    }
  }

  /** If the user cancels brand kit creation mid-process */
  const onCancelProcessing = () => {
    abortController.current.abort('cancel')
    abortController.current = new AbortController()
    // Reset processing state to return to BrandKitForm
    setIsProcessing(false)
  }

  /**
   * Common error handling for all brand kit actions
   */
  const handleError = (err: unknown) => {
    // If it's an AbortError, ignore
    if (err instanceof DOMException && err.name === 'AbortError') return
    reportError(err)
    showErrorMessageToast('An error occurred while creating your Brand Kit.')
  }

  // -----------
  // Rendering
  // -----------
  // 1. If brand kit creation is actively running, show the "Processing" UI
  if (isProcessing || createBrandKit.isLoading) {
    return <Processing onCancel={onCancelProcessing} websiteUrl={url} cancelOnError={false} />
  }

  // 2. Build up the context value for children
  const contextValue = {
    url,
    setUrl,
    isDirty,
    setIsDirty,
    isValid,
    errorMessage,
    isBlank,
    inputProps,
    onGenerateFromWebsite,
    onGenerateFromExampleBrand,
    onGenerateFromIntegration,
  }

  // 3. If integration connection failed, show fallback UI
  if (failedIntegration) {
    return (
      <BrandKitContext.Provider value={contextValue}>
        <IntegrationFailedBrandKitForm
          failedIntegration={failedIntegration}
          title={showTitles ? `Unable to connect to ${integrationToName[failedIntegration.provider]}` : undefined}
        />
      </BrandKitContext.Provider>
    )
  }

  // 4. If website analysis failed, show fallback UI
  if (websiteAnalysisFailed) {
    return (
      <BrandKitContext.Provider value={contextValue}>
        <WebsiteFailedBrandKitForm
          title={showTitles ? "Sorry, we couldn't analyze your website" : undefined}
          onSkip={onSkip}
          useIntegrations={useIntegrations}
        />
      </BrandKitContext.Provider>
    )
  }

  // 5. If we need more data, show the "Needs More Data" UI
  if (needsMoreData && brandKit && !isMobile && !isGeneratingFromExampleBrand && useWebsiteCrawler) {
    return (
      <ImagesProvider initialMaxPhotos={6}>
        <BrandKitContext.Provider value={contextValue}>
          <BrandKitMultiplePagesForm
            brandKit={brandKit}
            onSubmit={onSubmit}
            withinOnboarding={withinOnboarding}
            onBack={onBack}
            onNext={onNext}
            onSkip={onSkip}
            title={withinOnboarding ? 'We just need a bit more content to build your Brand Kit.' : undefined}
          />
        </BrandKitContext.Provider>
      </ImagesProvider>
    )
  }

  // 6. Otherwise, show the normal brand kit form
  return (
    <BrandKitContext.Provider value={contextValue}>
      <BrandKitForm
        useIntegrations={useIntegrations}
        platform={platform}
        onBack={onBack}
        onSkip={onSkip}
        withinOnboarding={withinOnboarding}
        title={showTitles ? 'Enter your website' : undefined}
      />
    </BrandKitContext.Provider>
  )
}
